'use client';

import { motion } from 'framer-motion';
import type React from 'react';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import { InfoContent } from './InfoContent';
import { TableTop10 } from './TableTop10';

import { Button, Modal } from '@/components';
import {
  MainBox,
  MainHome,
  TourneyList,
} from '@/components/GenericsTournaments';
import { IconAtention } from '@/components/IconAtention';
import ModalTkred from '@/components/ModalTkred';
import { TabType } from '@/components/Types';
import useAuth from '@/hooks/useAuth';
import useFriendsCup from '@/hooks/useFriendsCup';
import { useIsDesktop } from '@/hooks/useIsDesktop';
import { useIsLastMatchDay } from '@/hooks/useIsLastMatchDate';
import useToast from '@/hooks/useToast';
import useTourney from '@/hooks/useTourney';
import ROUTES from '@/router/routes';
import {
  ActionTypeManageFCupEnum,
  requestPostulantManagementFCup,
  getCdaBasicDataById,
  acceptCdaInvitation,
  requestPostulationFCup,
} from '@/services/friendsCup';
import type {
  BasicDataGDA,
  ListFCup,
  ListItemFCup,
  ResponseTop10,
} from '@/types/friendCup';
import type { ActionsTourneys, GenericItemTourneysData } from '@/types/tda';
import { EnumContextGDT } from '@/types/team';
import {
  cleanStorageRedirectInvitation,
  cn,
  FADE_IN_OUT,
  GENERICS_AMOUNTS,
  getParticipants,
  STATUS_TOURNEY_INVITATION,
  TEXTS,
} from '@/utils';
import { STATUS_INVITATION_FCUP } from '@/utils/constants';

type Props = {
  isMounted: boolean;
  refetchFather: () => void;
  pendingList: ListFCup | [];
  playingList: ListFCup | [];
  top10List?: ResponseTop10;
  invitationList: ListFCup | [];
  tabSelected: TabType;
  tabs?: React.ReactNode;
};

export type TypesState = {
  showModalInfo: boolean;
  nameUser: string;
  showModalRequest: boolean;
  idGroup: number | undefined;
  // Nuevos campos para manejar invitaciones desde URL
  showModalInvitationFromUrl: boolean;
  invitationDetails: BasicDataGDA | null;
  processingInvitation: boolean;
  idMiniLiga?: number | undefined;
};

export const initialState = {
  showModalInfo: false,
  nameUser: '',
  showModalRequest: false,
  idGroup: undefined,
  // Inicializar nuevos campos
  showModalInvitationFromUrl: false,
  invitationDetails: null,
  processingInvitation: false,
};

export const MainContent: React.FC<Props> = ({
  isMounted,
  pendingList,
  playingList,
  invitationList,
  refetchFather,
  tabs,
  tabSelected,
  top10List,
}) => {
  const navigate = useNavigate();
  const { isDesktop, isMobile } = useIsDesktop();
  const toast = useToast();
  const fetchedRef = useRef(false);

  const [state, setState] = useState<TypesState>(initialState);
  const { tourneyData, handleSetTourney } = useTourney();
  const { userInitialData: userLogged } = useAuth();
  const { groupData, handleSetGroup } = useFriendsCup();
  const [isAlreadyInvited, setIsAlreadyInvited] = useState(false);
  const dataPostLoginTkred =
    sessionStorage.getItem('postLoginTkred') || undefined;

  // Verificar si hay una invitación pendiente al cargar el componente
  useEffect(() => {
    const checkPendingInvitation = () => {
      const storedInvitationId = localStorage.getItem('invitationId');
      const storedInvitationType = localStorage.getItem('invitationType');
      const storedInvitationUrl = localStorage.getItem('invitationUrl');

      if (
        storedInvitationId &&
        storedInvitationType === 'copa' &&
        storedInvitationUrl
      ) {
        const invitationId = Number(storedInvitationId);

        // Verificar si ya estamos participando en este torneo
        const alreadyPlaying = playingList?.some(
          (item) => item.id === invitationId
        );

        // Verificar si ya tenemos una invitación pendiente para este torneo
        // Verificamos en ambas listas de pendientes
        const alreadyInvited = invitationList?.some(
          (item) => item.id === invitationId
        );
        setIsAlreadyInvited(alreadyInvited || false);

        // Verificar si estamos postulados a ese torneo
        const alreadyPostulated = pendingList?.some(
          (item) => item.id === invitationId
        );

        if (alreadyPlaying || alreadyPostulated) {
          // Asegurarnos de que el modal no se muestre
          setState(initialState);
          // Si ya estamos participando o postulados, limpiar el localStorage
          if (localStorage.getItem('invitationType') === 'copa') {
            cleanStorageRedirectInvitation();
          }
          // Mostrar un mensaje informativo
          if (alreadyPlaying) {
            toast.show('Ya estás participando en este equipo');
          } else if (alreadyPostulated) {
            toast.show(
              'Ya estás postulado al equipo, actualmente estás a la espera de que te acepte el dueño.'
            );
          }
        } else {
          setState((prevState) => ({
            ...prevState,
            showModalInvitationFromUrl:
              isMounted && !alreadyPlaying && !alreadyPostulated,
            idGroup: Number(invitationId),
          }));
        }
      }
    };

    checkPendingInvitation();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pendingList, playingList, invitationList]);

  const isLastMatchDay = useIsLastMatchDay();

  const cleanAfterInvitationUrl = () => {
    cleanStorageRedirectInvitation();
    setState(initialState);
  };

  // Obtener detalles de la invitación cuando se muestra el modal
  useEffect(() => {
    const fetchInvitationDetails = async () => {
      const txtError = 'El id del equipo es inválido';
      if (
        state.showModalInvitationFromUrl &&
        state.idGroup &&
        !fetchedRef.current
      ) {
        fetchedRef.current = true;
        try {
          const response = await getCdaBasicDataById(state.idGroup);

          if (!response.estado) {
            console.error(txtError);
            toast.show(txtError);
            cleanAfterInvitationUrl();
            return;
          }

          setState((prevState) => ({
            ...prevState,
            invitationDetails: response,
            nameUser:
              response.nombreDuenio
                ?.concat(' ')
                .concat(
                  response.apellidoDuenio ? response.apellidoDuenio : ''
                ) ?? 'Alguien',
          }));
        } catch (error) {
          toast.show(txtError);
          cleanAfterInvitationUrl();
          throw new Error(txtError);
        }
      }
    };

    fetchInvitationDetails();
  }, [state.showModalInvitationFromUrl, state.idGroup, toast]);

  const txtCenteredDesktop = useCallback((item: ListItemFCup) => {
    const isFull = item?.estadoGrupo === 'COMPLETO';

    return (
      <span
        className={cn(
          'text-2xs',
          !isFull ? 'text-gdt-status-red' : 'text-gdt-status-green'
        )}
      >
        {!isFull ? (
          <div className="flex items-center">
            <IconAtention className="mr-2" color={'gdt-status.red.DEFAULT'} />{' '}
            <span>
              {getParticipants(
                item?.cantidadDeParticipantes,
                GENERICS_AMOUNTS.MAX_PLAYERS_FCUP
              )}
            </span>
          </div>
        ) : (
          'Completo'
        )}
      </span>
    );
  }, []);

  const invitationDataList = useMemo((): GenericItemTourneysData[] | [] => {
    if (!invitationList) return [];
    return invitationList.map((item: ListItemFCup) => {
      return {
        id: item?.id,
        title: item?.nombreGrupo,
        subtitle: item?.nombreCreadorGrupo,
        disabled: item.idEstadoGrupo === 2,
        secundaryText: isDesktop
          ? item?.nombreCreadorGrupo
          : getParticipants(
              item?.cantidadDeParticipantes,
              GENERICS_AMOUNTS.MAX_PLAYERS_FCUP
            ),
        textCentered: isDesktop ? txtCenteredDesktop(item) : undefined,
      };
    });
  }, [isDesktop, invitationList, txtCenteredDesktop]);

  const disablingAccess = useCallback(
    (item: ListItemFCup): boolean => {
      if (item.estadoGrupo === 'COMPLETO') {
        return false;
      } else if (item.idUsuarioCreador === userLogged?.idUsuarioDT) {
        return false;
      }
      return true;
    },
    [userLogged?.idUsuarioDT]
  );

  const playingDataList = useMemo((): GenericItemTourneysData[] | [] => {
    if (!playingList) return [];

    return playingList.map((item: ListItemFCup) => {
      return {
        id: item?.id,
        title: item?.nombreGrupo,
        disabled: disablingAccess(item),
        secundaryText: isMobile
          ? getParticipants(
              item?.cantidadDeParticipantes,
              GENERICS_AMOUNTS.MAX_PLAYERS_FCUP
            )
          : undefined,
        textCentered: isDesktop ? txtCenteredDesktop(item) : undefined,
      };
    });
  }, [disablingAccess, isDesktop, isMobile, playingList, txtCenteredDesktop]);

  const pendingDataList = useMemo((): GenericItemTourneysData[] | [] => {
    if (!pendingList) return [];

    return pendingList.map((item: ListItemFCup) => {
      return {
        id: item?.id,
        title: item?.nombreGrupo,
        subtitle: item?.nombreCreadorGrupo,
        status:
          item?.estadoParticipante === STATUS_INVITATION_FCUP.RECHAZADO
            ? STATUS_TOURNEY_INVITATION.REJECTED
            : STATUS_TOURNEY_INVITATION.PENDING,
        secundaryText: isMobile
          ? getParticipants(
              item?.cantidadDeParticipantes,
              GENERICS_AMOUNTS.MAX_PLAYERS_FCUP
            )
          : undefined,
        textCentered: isDesktop ? txtCenteredDesktop(item) : undefined,
      };
    });
  }, [isDesktop, isMobile, pendingList, txtCenteredDesktop]);

  // Manejar cierre del modal de invitación desde URL
  const handleCloseInvitationModal = () => {
    // Limpiar localStorage
    if (localStorage.getItem('invitationType') === 'copa') {
      cleanStorageRedirectInvitation();
    }
    // Resetear estado
    setState((prevState) => ({
      ...prevState,
      showModalInvitationFromUrl: false,
      invitationDetails: null,
    }));

    fetchedRef.current = false;
  };

  //  TRATAR ACEPTAR/RECHAZAR INVITACIONES RECIBIDAS
  const onHandleRequestActions = useCallback(
    async (actionType: ActionTypeManageFCupEnum, idGroup?: number) => {
      if (state.processingInvitation) {
        return;
      }

      setState((prevState) => ({
        ...prevState,
        processingInvitation: true,
      }));

      try {
        // Usar la nueva función acceptCdaInvitation para aceptar invitaciones
        if (
          actionType === ActionTypeManageFCupEnum.ACC_ACEPTAR_INVITACION &&
          idGroup
        ) {
          const response = await acceptCdaInvitation(idGroup);

          if (response.estado && isAlreadyInvited) {
            toast.show(
              'Tenías una invitación pendiente para el equipo ' +
                state.invitationDetails?.nombre +
                '. Ya estás participando.'
            );
          } else if (response.estado) {
            toast.show('Gracias por aceptar la invitación.');
          } else {
            // Mostrar el mensaje de error específico devuelto por la API
            console.error('Error al aceptar la invitación:', response.mensaje);
            toast.show(response.mensaje || TEXTS.defaultError);
          }
        } else {
          // Para otras acciones, seguir usando la función existente
          const responseManagePostulant = await requestPostulantManagementFCup({
            tipoAccion: actionType,
            idGrupo: idGroup || 0,
          });

          if (responseManagePostulant.estado) {
            if (
              actionType === ActionTypeManageFCupEnum.ACC_RECHAZAR_INVITACION
            ) {
              // toast.show('Has rechazado la invitación');
            }
          } else {
            // Mostrar el mensaje de error específico devuelto por la API
            console.error('Error:', responseManagePostulant.mensaje);
            toast.show(responseManagePostulant.mensaje || TEXTS.defaultError);
          }
        }
      } catch (error) {
        console.error(error);
        toast.show(TEXTS.defaultError);
      } finally {
        // Limpiar localStorage si la invitación vino de una URL
        if (localStorage.getItem('invitationType') === 'copa') {
          cleanStorageRedirectInvitation();
        }

        setState(initialState);
        fetchedRef.current = false;
        refetchFather();
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [refetchFather, toast, state.processingInvitation]
  );

  // VER MODAL SOLICITUD RECIBIDA
  const onHandleRequestRecived = useCallback(
    async (params: ActionsTourneys) => {
      const item = invitationDataList?.find((user) => user.id === params.id);

      if (!item?.id) return;

      if (isMobile) {
        setState((prevState: TypesState) => {
          return {
            ...prevState,
            showModalRequest: true,
            nameUser: String(item?.subtitle),
            idGroup: item.id,
          };
        });
      } else {
        if (params.action === 'reject') {
          onHandleRequestActions(
            ActionTypeManageFCupEnum.ACC_RECHAZAR_INVITACION,
            item.id
          );
        } else {
          onHandleRequestActions(
            ActionTypeManageFCupEnum.ACC_ACEPTAR_INVITACION,
            item.id
          );
        }
      }
    },
    [isMobile, onHandleRequestActions, invitationDataList]
  );

  const RequestRecivedListUI = useCallback(() => {
    return (
      invitationDataList.length > 0 && (
        <TourneyList
          action={{
            txtAction: 'Ver solicitud',
            variantButton: 'secondary',
            txtActionDisabled: 'Ver solicitud',
          }}
          className="lg:mt-9 mt-6"
          classPaginator="paginator-sidebar"
          disabled={isLastMatchDay}
          handleSubmit={(params) => {
            if (isMobile) {
              params.id && onHandleRequestRecived(params);
            } else {
              params.action && onHandleRequestRecived(params);
            }
          }}
          list={invitationDataList}
          paginator={isDesktop}
          title="Te invitaron a participar"
          variant={isMobile ? 'default' : 'two-actions'}
        />
      )
    );
  }, [
    invitationDataList,
    isDesktop,
    isLastMatchDay,
    isMobile,
    onHandleRequestRecived,
  ]);

  // IR A VER TORNEOS
  const onHandlePlaying = useCallback(
    ({ id }: ActionsTourneys) => {
      navigate(ROUTES.friendsCup.viewTeam);

      handleSetTourney({
        ...tourneyData,
        id,
      });
      handleSetGroup({
        ...groupData,
        idGrupo: id,
      });
    },
    [groupData, handleSetGroup, handleSetTourney, navigate, tourneyData]
  );

  // Función para manejar invitaciones desde el modal (por link)
  const onHandleLinkInvitation = async (idGroup: number) => {
    if (state.processingInvitation) {
      return;
    }

    setState((prevState) => ({
      ...prevState,
      processingInvitation: true,
    }));

    try {
      // Para invitaciones por link
      const responsePostulation = await requestPostulationFCup(idGroup);

      if (responsePostulation.estado) {
        toast.show(
          'Enviaste tu solicitud para unirte al Equipo ' +
            state.invitationDetails?.nombre +
            '.'
        );
      } else {
        // Mostrar el mensaje específico del backend
        if (responsePostulation.mensaje) {
          toast.show(responsePostulation.mensaje);
        } else {
          toast.show(TEXTS.defaultError);
        }
      }
    } catch (error) {
      console.error(error);
      toast.show(TEXTS.defaultError);
    } finally {
      // Limpiar el estado y localStorage
      if (localStorage.getItem('invitationType') === 'copa') {
        cleanStorageRedirectInvitation();
      }

      // Resetear completamente el estado
      setState(initialState);
      fetchedRef.current = false; // Resetear el ref
      // Refrescar los datos
      refetchFather();
    }
  };

  const ContentTeam = useCallback(() => {
    return (
      <>
        {/* TE INVITARON A PARTICIPAR */}
        <RequestRecivedListUI />

        {/* EQUIPOS */}
        {playingDataList.length > 0 && (
          <TourneyList
            key={playingDataList.length}
            action={{
              txtAction: 'Ver Equipo',
              variantButton: 'secondary',
              txtActionDisabled: 'Ver Equipo',
            }}
            className="lg:mt-9 mt-6"
            handleSubmit={onHandlePlaying}
            list={playingDataList}
            paginator={isDesktop}
            title="Estás participando"
          />
        )}

        {/* LISTA TORNEOS ESPERANDO ACEPTACION */}
        {pendingDataList.length > 0 && (
          <TourneyList
            className="lg:mt-9 mt-6"
            list={pendingDataList}
            paginator={isDesktop && true}
            title="Esperando aceptación"
            variant="show-status"
          />
        )}
      </>
    );
  }, [
    RequestRecivedListUI,
    isDesktop,
    isMobile,
    onHandlePlaying,
    pendingDataList,
    playingDataList,
  ]);

  const ContentTop10 = useCallback(() => {
    if (top10List && top10List.length > 0) {
      return <TableTop10 top={top10List} />;
    }
  }, [top10List]);

  const spanStyle: React.CSSProperties = {
    display: 'block',
    textAlign: 'left',
    whiteSpace: 'normal',
    wordBreak: 'break-word',
    maxWidth: '98%',
    margin: '0 auto',
  };

  return (
    <MainHome
      className="w-full"
      context={EnumContextGDT.FriendsCup}
      subtitle={
        isDesktop ? (
          'Creá un equipo, invitá a 3 amigos y competí contra otros equipos. Tu equipo suma con los puntos de tus 11 y el de tus 3 amigos.'
        ) : (
          <span style={spanStyle}>
            Creá un equipo, invitá a 3 amigos y competí contra otros equipos. Tu
            equipo suma con los puntos de tus 11 y el de tus 3 amigos.
          </span>
        )
      }
      title="Copa de Amigos"
    >
      {isMounted && (
        <>
          {isDesktop && <div className="justify-center flex mt-9">{tabs}</div>}

          <motion.div
            key={tabSelected}
            animate={'show'}
            initial="hide"
            variants={FADE_IN_OUT}
          >
            {tabSelected === TabType.FirstTab ? (
              <ContentTeam />
            ) : (
              <ContentTop10 />
            )}

            {/* CAJA PRINCIPAL DESKTOP SIN DATOS */}
            {isDesktop &&
              invitationDataList.length <= 0 &&
              playingDataList.length <= 0 &&
              pendingDataList.length <= 0 &&
              tabSelected === TabType.FirstTab && (
                <MainBox className="!mt-6" context={EnumContextGDT.FriendsCup}>
                  <p className="text-gdt-gray-2">
                    Todavía no estás participando de ningún
                    <br />
                    equipo.{' '}
                    <strong>
                      Creá uno nuevo o inscribite a<br />
                      uno de los recomendados.
                    </strong>
                  </p>
                  <Button
                    className="w-[187px] mt-8 mx-auto"
                    disabled={!tourneyData.inscriptionOpen}
                    type="button"
                    variant="primary"
                    onClick={() => {
                      if (userLogged?.dtExtranjero) {
                        return toast.show(TEXTS.exclusiveForArgentinianDTs);
                      }
                      navigate(ROUTES.friendsCup.createTeam);
                    }}
                  >
                    Crear nuevo equipo
                  </Button>
                  <Button
                    className="mt-8 mx-auto font-bold"
                    type="button"
                    variant="text"
                    onClick={() =>
                      handleSetTourney({ ...tourneyData, showModalInfo: true })
                    }
                  >
                    Más información
                  </Button>
                </MainBox>
              )}
          </motion.div>
          {/* MODAL INFO */}
          <Modal
            className="w-[500px] text-center px-[38px]"
            controlledOnClose={() =>
              handleSetTourney({ ...tourneyData, showModalInfo: false })
            }
            isControlledOpen={Boolean(tourneyData.showModalInfo) && isDesktop}
            render={() => <InfoContent />}
            size="sm"
            variant="controlled"
          />

          {/* MODAL ACEPTACION ACEPTACION/RECHAZO INVITACION RECIBIDAS */}
          <Modal
            controlledOnClose={() =>
              setState({ ...state, showModalRequest: false })
            }
            isControlledOpen={state.showModalRequest && isMobile}
            render={() => {
              return (
                <div className="text-center mt-2">
                  <p className="text-base font-medium">
                    <strong>{state.nameUser}</strong> te invitó a
                    <br />
                    participar de su Copa de Amigos
                  </p>
                  <div className="flex space-x-2 my-10">
                    <Button
                      className="flex-1"
                      type="button"
                      variant="secondary"
                      onClick={() =>
                        onHandleRequestActions(
                          ActionTypeManageFCupEnum.ACC_RECHAZAR_INVITACION,
                          state.idGroup
                        )
                      }
                    >
                      Rechazar
                    </Button>
                    <Button
                      className="flex-1"
                      type="button"
                      variant="primary"
                      onClick={() =>
                        onHandleRequestActions(
                          ActionTypeManageFCupEnum.ACC_ACEPTAR_INVITACION,
                          state.idGroup
                        )
                      }
                    >
                      Aceptar
                    </Button>
                  </div>
                </div>
              );
            }}
            variant="controlled"
          />

          {/* MODAL PARA INVITACIONES DESDE URL */}
          <Modal
            controlledOnClose={handleCloseInvitationModal}
            isControlledOpen={Boolean(
              state.showModalInvitationFromUrl &&
                !state.processingInvitation &&
                state.invitationDetails
            )}
            render={() => {
              return (
                <div className="text-center mt-2">
                  <p className="text-base text-[#666666]">
                    <strong>{state.nameUser}</strong> te invitó a
                    <br />
                    postularte a su Equipo
                  </p>
                  <div className="flex space-x-2 my-6">
                    <Button
                      className="flex-1 !bg-[#EEF2FF] !text-[#3366FF] hover:!bg-[#E5E9FF]"
                      type="button"
                      variant="secondary"
                      onClick={handleCloseInvitationModal}
                    >
                      Rechazar
                    </Button>
                    <Button
                      className="flex-1 !bg-[#3366FF] hover:!bg-[#2952CC]"
                      type="button"
                      variant="primary"
                      onClick={
                        isAlreadyInvited
                          ? () =>
                              onHandleRequestActions(
                                ActionTypeManageFCupEnum.ACC_ACEPTAR_INVITACION,
                                state.idGroup
                              )
                          : () => onHandleLinkInvitation(state.idGroup ?? 0)
                      }
                    >
                      Aceptar
                    </Button>
                  </div>
                </div>
              );
            }}
            variant="controlled"
          />

          {/* MODAL PARA INVITACIONES DESDE TKRED */}
          {dataPostLoginTkred && (
            <ModalTkred
              data={dataPostLoginTkred}
              handleRequestActions={onHandleRequestActions}
            />
          )}
        </>
      )}
    </MainHome>
  );
};
